import { Moment } from 'moment'

import { ConfigItem } from 'Core/interfaces'

import { ExceptionFormType } from 'Portals/Internal/entities/CustomerInvoice'

export enum InvoiceLoaderLocationType {
	ServiceLocation = 'Service Location',
	SupplierLocation = 'Supplier Location'
}

export enum InvoiceLoaderSearchResultFields {
	searchResultById = 'searchResultById',
	searchResultBySupplierLocationId = 'searchResultBySupplierLocationId',
	searchResultBySupplierLocationName = 'searchResultBySupplierLocationName',
	searchResultBySupplierInvoiceNumber = 'searchResultBySupplierInvoiceNumber'
}

export enum ServiceLocationSearchFields {
	searchResultById = 'searchResultById',
	searchResultByName = 'searchResultByName'
}

export enum CompanySearchFields {
	searchResultById = 'searchById',
	searchResultByName = 'searchByName'
}

export enum SupplierLocationSearchFields {
	supplierLocationIds = 'supplierLocationIds',
	supplierLocationNames = 'supplierLocationNames'
}

export enum InvoiceLoaderFilterFields {
	supplierInvoiceDateFrom = 'invoiceDate',
	statusCode = 'statusCode',
	assigneeId = 'assigneeId'
}

export enum InvoiceLoaderStatusName {
	rejected = 'Rejected',
	indexed = 'Indexed',
	loaderReady = 'Loader Ready',
	cancelled = 'Cancelled',
	complete = 'Completed',
	loaderReadyException = 'Loader Ready-Exception',
	indexedException = 'Indexed-Exception',
	movedToQueue = 'Moved to Queue'
}

export enum InvoiceLoaderLabels {
	BarCode = 'barCode',
	InvoiceNumber = 'invoiceNumber',
	InvoiceDate = 'invoiceDate',
	TotalAmount = 'totalAmount',
	Assignee = 'assignee',
	ServiceDate = 'serviceDate',
	Status = 'status',
	Currency = 'currency',
	FileName = 'fileName',
	SelectedServiceLocation = 'selectedServiceLocation',
	ServiceLocation = 'serviceLocation',
	SelectedSupplierLocation = 'selectedSupplierLocation',
	SupplierLocation = 'supplierLocation'
}

export enum InvoiceLoaderItemFields {
	Id = 'id',
	Description = 'description',
	UnitOfMeasure = 'unitOfMeasure',
	Quantity = 'quantity',
	UnitPrice = 'unitPrice',
	ServiceDate = 'serviceDate',
	Bol = 'bol',
	Total = 'total'
}

export declare namespace InvoiceLoader {
	interface Basic {
		assignee: Entity | null
		indexId: number
		lastUpdateDate: string
		lastUpdatedBy: Entity | null
		mappedItemAmount: number
		source: string
		status: NameCode<InvoiceLoaderStatusName> | null
		supplierInvoiceDate: string
		supplierInvoiceNumber: string
		supplierLocation: Entity | null
		totalInvoiceAmount: number
		uploadedBy: Entity
		uploadedDate: string
	}

	interface MovedToQueueBy {
		movedToQueueDate: string
		name: string
	}

	interface Preview {
		assignee: Entity
		barCode: string
		fileName: string
		id: number
		invoiceDate: string
		itemMappedTotalAmount?: number
		lastUpdateDate: string
		movedToQueueBy: MovedToQueueBy
		scanRecordId: number
		serviceSite: Entity | null
		status: Entity<InvoiceLoaderStatusName>
		supplierInvoiceNumber: string
		supplierLocation: Entity | null
		totalInvoiceAmount: number
	}

	interface Detailed {
		id: number
		file: Entity | null
		status: NameCode<InvoiceLoaderStatusName> | null
		currency: string
		barCode: number
		invoiceDate: string
		supplierInvoiceNumber: string
		serviceDate: string
		totalInvoiceAmount: number
		assignee: Entity | null
		serviceLocation: [ServiceLocationAddress]
		supplierLocation: SupplierLocationAddress
		items: Item[]
	}

	interface Item {
		[InvoiceLoaderItemFields.Id]: BasicNumber
		[InvoiceLoaderItemFields.Description]: BasicString
		[InvoiceLoaderItemFields.UnitOfMeasure]: BasicString
		[InvoiceLoaderItemFields.Quantity]: BasicString
		[InvoiceLoaderItemFields.UnitPrice]: BasicString
		[InvoiceLoaderItemFields.ServiceDate]: BasicString | Moment
		[InvoiceLoaderItemFields.Bol]: BasicString
		[InvoiceLoaderItemFields.Total]: BasicString
	}

	interface SupplierLocationAddress extends Address {
		selectedSupplierLocation: Entity | null
	}

	interface ServiceLocationAddress extends Address {
		selectedServiceLocation: Entity | null
	}

	namespace Search {
		interface Config {
			[InvoiceLoaderSearchResultFields.searchResultById]: ConfigItem<InvoiceLoaderSearchResultFields>
			[InvoiceLoaderSearchResultFields.searchResultBySupplierLocationId]: ConfigItem<InvoiceLoaderSearchResultFields>
			[InvoiceLoaderSearchResultFields.searchResultBySupplierLocationName]: ConfigItem<InvoiceLoaderSearchResultFields>
			[InvoiceLoaderSearchResultFields.searchResultBySupplierInvoiceNumber]: ConfigItem<InvoiceLoaderSearchResultFields>
		}
		interface Result {
			[InvoiceLoaderSearchResultFields.searchResultById]: Entity[]
			[InvoiceLoaderSearchResultFields.searchResultBySupplierLocationId]: Entity[]
			[InvoiceLoaderSearchResultFields.searchResultBySupplierLocationName]: Entity[]
			[InvoiceLoaderSearchResultFields.searchResultBySupplierInvoiceNumber]: Entity[]
		}
	}

	interface Address {
		address: BasicString
		city: BasicString
		name: string
		postalCode: BasicString
		state: BasicString
		selectedServiceLocation: Entity | null
		selectedSupplierLocation: Entity | null
	}

	namespace Form {
		interface ServiceLocationConfig {
			[ServiceLocationSearchFields.searchResultById]: ConfigItem<ServiceLocationSearchFields>
			[ServiceLocationSearchFields.searchResultByName]: ConfigItem<ServiceLocationSearchFields>
		}

		interface SupplierLocationConfig {
			[SupplierLocationSearchFields.supplierLocationIds]: ConfigItem<ServiceLocationSearchFields>
			[SupplierLocationSearchFields.supplierLocationNames]: ConfigItem<ServiceLocationSearchFields>
		}

		interface Edit {
			[InvoiceLoaderLabels.SelectedServiceLocation]: BasicString
			[InvoiceLoaderLabels.SelectedSupplierLocation]: BasicString
			[InvoiceLoaderLabels.BarCode]: BasicString
			[InvoiceLoaderLabels.InvoiceNumber]: BasicString
			[InvoiceLoaderLabels.InvoiceDate]: Moment | null
			[InvoiceLoaderLabels.TotalAmount]: BasicString
			[InvoiceLoaderLabels.Assignee]: LabelValue<number> | null
			[InvoiceLoaderLabels.ServiceDate]: Moment | null
			items: Item.Edit[]
		}

		interface Request {
			serviceLocationId: BasicNumber
			supplierLocationId: BasicNumber
			barCode: BasicString
			supplierInvoiceNumber: BasicString
			assigneeId: BasicNumber
			invoiceDate: BasicString
			totalInvoiceAmount: BasicString
			serviceDate: BasicString
			items: Item.Request[]
		}

		namespace Item {
			interface Edit {
				[InvoiceLoaderItemFields.Id]: number
				[InvoiceLoaderItemFields.Description]: BasicString
				[InvoiceLoaderItemFields.UnitOfMeasure]: BasicString
				[InvoiceLoaderItemFields.Quantity]: BasicString
				[InvoiceLoaderItemFields.UnitPrice]: BasicString
				[InvoiceLoaderItemFields.ServiceDate]: Moment | null
				[InvoiceLoaderItemFields.Bol]: BasicString
				[InvoiceLoaderItemFields.Total]: BasicString
			}

			interface Request {
				[InvoiceLoaderItemFields.Id]: number
				[InvoiceLoaderItemFields.Description]: BasicString
				[InvoiceLoaderItemFields.UnitOfMeasure]: BasicString
				[InvoiceLoaderItemFields.Quantity]: BasicNumber
				[InvoiceLoaderItemFields.UnitPrice]: BasicNumber
				[InvoiceLoaderItemFields.ServiceDate]: BasicString
				[InvoiceLoaderItemFields.Bol]: BasicString
				[InvoiceLoaderItemFields.Total]: BasicNumber
			}
		}
	}

	namespace Exception {
		interface Basic {
			id: number
			scanException: Entity | null
			scanExceptionType: Entity | null
			contactId: number
			contactFirstName: string
			contactLastName: string
			assignedUserId: number
			assignedUserFirstName: string
			assignedUserLastName: string
			routeQueueCode: string
			comment: string
			isResolved: boolean
			commentDate: string
		}

		namespace Form {
			interface Add {
				exceptionFormType: ExceptionFormType
				exceptionTypeId: BasicString
				scanExceptionId: BasicString
				comment: string
			}
		}

		namespace Request {
			interface Add {
				scanExceptionId: BasicNumber
				additionalComments: BasicString
			}
		}
	}

	namespace Comment {
		namespace Request {
			interface Add {
				comment: BasicString
			}
		}
	}

	namespace LoaderReady {
		interface Detailed {
			id: number
			supplierInvoiceNumber: BasicString
			status: Entity | null
			supplierLocation: SupplierLocationAddress
			serviceSite: ServiceLocationAddress
			barCode: BasicNumber
			invoiceDate: BasicString
			lastUpdateDate: BasicString
			totalInvoiceAmount: BasicNumber
			itemMappedTotalAmount: BasicNumber
			fileName: BasicString
			supplierName: string
			assignee: Entity | null
			movedToQueueBy: {
				movedToQueueDate: BasicString
				name: BasicString
			} | null
			scanRecordId: number
			source: BasicString
		}

		namespace UpdateLocation {
			interface Request {
				serviceLocationId: BasicNumber
				supplierLocationId: BasicNumber
			}
		}

		namespace Item {
			interface Basic {
				supplierInvoiceNumber: BasicString
				supplierInvoiceLoaderItemId: number
				supplierInvoiceLoaderItemName: BasicString
				serviceOccurrenceSupplierId: BasicNumber
				serviceDate: BasicString
				itemDescription: BasicString
				weight: BasicNumber
				quantity: BasicNumber
				unitOfMeasure: BasicString
				unitPrice: BasicNumber
				total: BasicNumber
				bol: BasicString
				serviceSiteId: BasicNumber
				item: Entity | null
				hasExistingServiceOccurrences: boolean
				isWeightMandatory: boolean
			}

			namespace Map {
				interface Basic {
					itemId: number
					name: BasicString
					cost: BasicString
					hasExisitingServiceOccurrences: boolean
					isDependingItem: boolean
				}

				interface Request {
					supplierInvoiceLoaderItemId: number
					itemId: Key | null
				}
			}

			namespace Match {
				interface Basic {
					serviceOccurenceSuplId: BasicNumber
					customerOrderId: BasicNumber
					serviceDate: BasicString
					customerOrderItemId: BasicNumber
					supplierDescription: BasicString
					cost: BasicString
					quantity: BasicNumber
					serviceOccurenceId: number
				}

				interface Request {
					supplierInvoiceLoaderItemId: number
					serviceOccurenceSuplId: Key | null
				}
			}

			interface Update {
				items: {
					id: Nullable<Key>
					weight: BasicNumber
					bol: BasicString
				}[]
				assigneeId: BasicNumber
			}
		}
	}
}
